import React, { useMemo, useState } from 'react';
import { BarChart3, Bot, Check, GitCompare, Loader2, Send, Sparkles, X } from 'lucide-react';
import { hopeLabCoverUrl } from './coverUrl';

export const DISCOVERY_MODES = [
    ['latest', 'Mới nhất'], ['hidden-gems', 'Mỏ vàng ẩn'], ['old-gold', 'Vàng cũ'],
    ['underrated', 'Đánh giá thấp'], ['fast-growing', 'Tăng nhanh'], ['litrpg', 'LitRPG'],
    ['progression-fantasy', 'Progression Fantasy'], ['dungeon-core', 'Dungeon Core'],
    ['sci-fi', 'Sci-Fi'], ['space-opera', 'Space Opera']
];

export function DiscoveryModes({ value, onChange }) {
    return <div className="hopelab-discovery-modes" aria-label="Chế độ khám phá">
        {DISCOVERY_MODES.map(([id, label]) => <button key={id} type="button" className={value === id ? 'is-active' : ''} onClick={() => onChange(id)}>{label}</button>)}
    </div>;
}

export function InsightsBar({ insights = [], embedding }) {
    if (!insights.length && !embedding?.used && !embedding?.error) return null;
    return <section className="hopelab-insights-panel">
        <div className="hopelab-insights-heading"><Sparkles size={16} /><div><strong>AI Insights</strong><span>Quan sát từ dữ liệu shortlist, không tự tạo dữ kiện</span></div></div>
        <div className="hopelab-insights-list">
            {insights.map((item, index) => <div key={index} className={`is-${item.level || 'info'}`}><strong>{item.title}</strong><span>{item.detail}</span></div>)}
            {embedding?.pending && <div className="is-info"><strong>Đang xếp hạng semantic</strong><span>Shortlist đã sẵn sàng; embedding chạy riêng để tránh timeout.</span></div>}
            {embedding?.used && <div className="is-positive"><strong>Semantic similarity đang bật</strong><span>Xếp hạng bằng {embedding.model}; metadata vẫn là lớp kiểm chứng.</span></div>}
            {embedding?.error && <div className="is-warning"><strong>Embedding tạm fallback</strong><span>{embedding.error} · HopeLab đang dùng hybrid metadata.</span></div>}
        </div>
    </section>;
}

const comparisonRows = [
    ['Publisher', book => book.audioPublisher || book.printPublisher || 'Chưa xác minh'],
    ['Reviews', book => Number(book.ratingsCount || 0).toLocaleString('en-US')],
    ['Rating', book => book.rating ? Number(book.rating).toFixed(1) : 'Chưa có'],
    ['Độ dài', book => book.duration || 'Chưa xác minh'],
    ['Series', book => (book.seriesCount || 1) + ' volume'],
    ['Trend', book => book.metricsGrowth > 0 ? (book.trendScore || 0) + '/100 · +' + book.metricsGrowth + ' ratings' : 'Đang tạo baseline'],
    ['Cạnh tranh', book => (book.competitionScore || 0) + '/100'],
    ['Opportunity', book => (book.opportunityScore || 0) + '/100'],
    ['Kênh BKT', book => (book.bktOpportunityScore || 0) + '/100'],
    ['Kênh nuôi', book => (book.farmOpportunityScore || 0) + '/100'],
    ['Semantic', book => book.semanticSimilarity != null ? book.semanticSimilarity + '/100' : 'Chưa chạy'],
    ['Độ tin cậy', book => (book.dataConfidence || 0) + '/100']
];

export function ComparisonModal({ books, onClose, onRemove }) {
    return <div className="hopelab-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
        <div className="hopelab-comparison-modal" role="dialog" aria-modal="true">
            <header><div><span>BOOK COMPARISON</span><h3>So sánh {books.length} series</h3><p>Điểm được tính từ dữ liệu, publisher policy và độ mới với team.</p></div><button onClick={onClose}><X size={20} /></button></header>
            <div className="hopelab-comparison-scroll"><table><thead><tr><th>Tiêu chí</th>{books.map(book => <th key={book.id}><div className="hopelab-compare-book">{book.cover && <img src={hopeLabCoverUrl(book.cover)} alt="" />}<span><strong>{book.seriesTitle || book.title}</strong><small>{book.author || 'Chưa xác minh tác giả'}</small></span><button onClick={() => onRemove(book.id)}><X size={13} /></button></div></th>)}</tr></thead>
            <tbody>{comparisonRows.map(([label, value]) => <tr key={label}><th>{label}</th>{books.map(book => <td key={book.id}>{value(book)}</td>)}</tr>)}</tbody></table></div>
            <footer><span>Rating/review hiện lấy từ nguồn metadata được ghi trên từng card.</span><button className="hopelab-secondary-button" onClick={onClose}>Đóng</button></footer>
        </div>
    </div>;
}

const suggestions = [
    'Tìm sách giống The Good Guys', 'Tránh Podium và Aethon',
    'Tìm LitRPG cũ nhưng fanbase còn hoạt động', 'Chỉ publisher độc lập',
    'Sách từ 2015-2020, rating trên 4.2'
];

export function CopilotDrawer({ open, onClose, apiFetch, books, onApplyFilters }) {
    const [question, setQuestion] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const recommended = useMemo(() => new Set(messages.flatMap(message => message.recommendedIds || [])), [messages]);
    if (!open) return null;
    const sendQuestion = async value => {
        const text = String(value || question).trim(); if (!text || loading) return;
        setQuestion(''); setLoading(true); setMessages(current => [...current, { role: 'user', text }]);
        try {
            const payload = await apiFetch('/api/hopelab/research', { method: 'POST', body: JSON.stringify({ action: 'copilot', question: text, books }) });
            setMessages(current => [...current, { role: 'assistant', text: payload.answer, filters: payload.filters || {}, recommendedIds: payload.recommendedIds || [] }]);
        } catch (error) {
            setMessages(current => [...current, { role: 'assistant', text: error.message, error: true }]);
        } finally { setLoading(false); }
    };
    return <aside className="hopelab-copilot-drawer">
        <header><div><Bot size={18} /><span><strong>HopeLab Copilot</strong><small>Hỏi bằng ngôn ngữ tự nhiên</small></span></div><button onClick={onClose}><X size={18} /></button></header>
        <div className="hopelab-copilot-suggestions">{suggestions.map(item => <button key={item} onClick={() => sendQuestion(item)}>{item}</button>)}</div>
        <div className="hopelab-copilot-messages">
            {!messages.length && <div className="hopelab-copilot-empty"><Bot size={28} /><strong>Copilot không tự bịa dữ liệu</strong><span>Nó chuyển câu hỏi thành bộ lọc và chỉ nhận xét các series đang có.</span></div>}
            {messages.map((message, index) => <div key={index} className={`hopelab-copilot-message is-${message.role} ${message.error ? 'is-error' : ''}`}><span>{message.text}</span>{message.role === 'assistant' && Object.keys(message.filters || {}).length > 0 && <button onClick={() => onApplyFilters(message.filters)}><Check size={13} /> Áp dụng bộ lọc</button>}</div>)}
            {loading && <div className="hopelab-copilot-message is-assistant"><Loader2 size={15} className="animate-spin" /> Đang đối chiếu shortlist…</div>}
        </div>
        {recommended.size > 0 && <div className="hopelab-copilot-recommended"><BarChart3 size={14} /> Copilot đã đánh dấu {recommended.size} series đáng xem trong câu trả lời.</div>}
        <form onSubmit={event => { event.preventDefault(); sendQuestion(); }}><input value={question} onChange={event => setQuestion(event.target.value)} placeholder="Ví dụ: sách cũ hơn 5 năm, tránh Podium…" /><button disabled={loading || !question.trim()}><Send size={16} /></button></form>
    </aside>;
}

export function CompareFloatingButton({ count, onClick }) {
    if (!count) return null;
    return <button className="hopelab-compare-floating" onClick={onClick}><GitCompare size={17} /> So sánh {count}/4</button>;
}

